import { inject, Injectable, Signal } from '@angular/core';
import { merge, Subject } from 'rxjs';
import { catchError, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { LoggingService } from '@lib-ui/lib/services/logging.service';
import { LoadingState, ServiceState } from '@lib-ui/lib/models';
import { RoleClient, RoleDto } from '@lib-ui/client';

interface RoleListState extends ServiceState {
  roles: Signal<RoleDto[]>;
}

@Injectable({
  providedIn: 'root',
})
export class RoleListService {
  private readonly roleClient = inject(RoleClient);
  private readonly loggingService = inject(LoggingService);
  private readonly load$ = new Subject<void>();
  private readonly error$ = new Subject<Error | null>();
  private readonly roles$ = this.load$.pipe(
    startWith(null),
    switchMap(() => {
      return this.roleClient.getAllRoles().pipe(
        catchError((error) => {
          this.error$.next(error);
          return [];
        }),
      );
    }),
    tap(roles => {
      this.loggingService.logTrace(roles);
    }),
    shareReplay(1),
  );
  private readonly status$ = merge(
    this.roles$.pipe(map(() => LoadingState.Success)),
    this.error$.pipe(map(() => LoadingState.Error)),
    this.load$.pipe(map(() => LoadingState.Pending)),
  );
  private readonly roles = toSignal(this.roles$, { initialValue: [] });
  private readonly status = toSignal(this.status$, { initialValue: LoadingState.Pending });
  private readonly error = toSignal(this.error$, { initialValue: null });
  public state: RoleListState = {
    roles: this.roles,
    status: this.status,
    error: this.error,
  };

}


